import { Link } from 'react-router-dom'

const effectiveDate = '25 September 2026'

export function Terms() {
  return <>
    <section className="legal-hero public-container">
      <span className="public-kicker">Legal</span>
      <h1>Terms of Service</h1>
      <p>
        These Terms govern access to and use of the Mail Percent website, workspaces, integrations and related
        services.
      </p>
      <span className="legal-effective">Effective {effectiveDate}</span>
    </section>

    <section className="legal-section public-container">
      <div className="legal-layout">
        <aside className="legal-summary">
          <strong>Using Mail Percent</strong>
          <p>
            Mail Percent is a business email-operations product. Workspace owners control who joins their organisation
            and which supported mailboxes are connected.
          </p>
          <div>
            <span>Product</span>
            <b>Customer email operations</b>
          </div>
          <div>
            <span>Primary users</span>
            <b>Organisations & teams</b>
          </div>
          <div>
            <span>Support contact</span>
            <a href="mailto:jonathanayabulela35@gmail.com">jonathanayabulela35@gmail.com</a>
          </div>
        </aside>

        <article className="legal-copy">
          <section>
            <h2>1. Acceptance of these Terms</h2>
            <p>
              By creating an account, joining a workspace, connecting a mailbox or otherwise using Mail Percent, you
              agree to these Terms. If you use Mail Percent on behalf of an organisation, you confirm that you have
              authority to bind that organisation to these Terms.
            </p>
          </section>

          <section>
            <h2>2. The service</h2>
            <p>
              Mail Percent provides tools for measuring and understanding customer-email operations, including
              response timing, queue pressure, workload, SLA Performance and team follow-through. The
              exact features available may change as the product develops.
            </p>
          </section>

          <section>
            <h2>3. Accounts and workspace administration</h2>
            <p>
              You must provide accurate account information and keep your credentials secure. You are responsible for
              activity performed through your account unless it results from a security failure attributable to Mail
              Percent.
            </p>
            <p>
              Workspace owners and authorised administrators may invite users, assign roles, configure SLA targets,
              connect organisation settings and manage access. Users joining a workspace understand that their
              authorised workspace administrators may be able to view operational measurements associated with their
              work account.
            </p>
          </section>

          <section>
            <h2>4. Connected email services</h2>
            <p>
              Mail Percent may integrate with third-party services such as Google Gmail and Microsoft Outlook. Those
              services are provided by their respective providers and remain subject to their own terms and privacy
              policies.
            </p>
            <p>
              You must only connect mailboxes and data that you are authorised to connect. Mail Percent may suspend
              an integration if required to protect users, comply with provider requirements or address security
              concerns.
            </p>
          </section>

          <section>
            <h2>5. Acceptable use</h2>
            <p>You may not use Mail Percent to:</p>
            <ul>
              <li>access a mailbox, workspace or account without permission;</li>
              <li>circumvent security, authentication or access controls;</li>
              <li>introduce malware, interfere with the service or attempt to overload its infrastructure;</li>
              <li>use the service in violation of applicable law or third-party rights;</li>
              <li>misrepresent Mail Percent measurements as guarantees of employee performance, legal compliance or customer outcomes.</li>
            </ul>
          </section>

          <section>
            <h2>6. Organisation data and responsibilities</h2>
            <p>
              Organisations are responsible for determining whether and how Mail Percent is appropriate for their
              workplace, including informing employees and other users where required by employment, privacy or data
              protection law.
            </p>
            <p>
              Mail Percent provides operational context, but the organisation remains responsible for decisions made
              using reports, SLA Performance or other measurements.
            </p>
          </section>

          <section>
            <h2>7. SLA Performance and reporting</h2>
            <p>
              Mail Percent metrics and SLA targets are intended to support operational visibility. They are not a
              guarantee of sales, customer retention, employee performance, service levels or any other business
              outcome. Queue pressure, workload, staffing, complexity and other context may affect measured results.
            </p>
          </section>

          <section>
            <h2>8. Fees and billing</h2>
            <p>
              If Mail Percent offers paid plans or paid features, the price, billing interval, taxes and material
              commercial terms presented at purchase or in an applicable order form will form part of these Terms.
              Unless otherwise stated, charges already incurred are non-refundable except where required by law.
            </p>
          </section>

          <section>
            <h2>9. Intellectual property</h2>
            <p>
              Mail Percent and its software, branding, interfaces, documentation and underlying technology are owned
              by or licensed to Mail Percent and are protected by applicable intellectual-property laws. These Terms
              do not transfer ownership of Mail Percent intellectual property to users.
            </p>
            <p>
              You retain rights you already have in information you submit to the service. You grant Mail Percent the
              limited rights necessary to host, process and use that information to operate and improve the service.
            </p>
          </section>

          <section>
            <h2>10. Availability and changes</h2>
            <p>
              We aim to keep Mail Percent reliable, but uninterrupted or error-free availability is not guaranteed.
              We may maintain, modify, add or remove features where reasonably necessary for product, security,
              provider or legal reasons.
            </p>
          </section>

          <section>
            <h2>11. Suspension and termination</h2>
            <p>
              You may stop using Mail Percent at any time. We may restrict or suspend access where reasonably
              necessary to address non-payment, misuse, security risk, legal obligations or material breach of these
              Terms.
            </p>
            <p>
              On termination, provisions that by their nature should survive — including intellectual property,
              payment obligations, disclaimers and limitations of liability — will continue to apply.
            </p>
          </section>

          <section>
            <h2>12. Disclaimers</h2>
            <p>
              To the extent permitted by applicable law, Mail Percent is provided on an “as available” basis. We do
              not warrant that measurements will be error-free or that use of the service will produce a particular
              commercial, employment or customer outcome.
            </p>
          </section>

          <section>
            <h2>13. Limitation of liability</h2>
            <p>
              To the extent permitted by applicable law, Mail Percent will not be liable for indirect, incidental,
              special or consequential losses arising from use of the service. Nothing in these Terms excludes or
              limits liability that cannot lawfully be excluded, including rights that may apply under South African
              consumer-protection law.
            </p>
          </section>

          <section>
            <h2>14. Governing law</h2>
            <p>
              These Terms are governed by the laws of the Republic of South Africa, without limiting any mandatory
              rights that apply to a user under another applicable law.
            </p>
          </section>

          <section>
            <h2>15. Changes to these Terms</h2>
            <p>
              We may update these Terms as the service changes. The current version will be published on this page
              with an updated effective date where appropriate. Material changes may also be communicated through the
              service or another reasonable channel.
            </p>
          </section>

          <section>
            <h2>16. Contact</h2>
            <p>
              Questions about these Terms can be sent to
              {' '}<a href="mailto:jonathanayabulela35@gmail.com">jonathanayabulela35@gmail.com</a>.
            </p>
            <p>
              Please also review the <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>
        </article>
      </div>
    </section>
  </>
}
